import type { FirstAccountSetupState } from "@cmmd-forge/contracts";

import { firstRunEnvironmentEntry } from "../lib/firstRunEnvironment";

/**
 * Whether the /setup route may leave for /new.
 *
 * A completed setup flag alone was not enough: useFirstAccountSetupRedirect
 * sends every other route back to /setup whenever the CMMD cloud catalog has
 * no Environment, regardless of that flag. setup.tsx used to read only the
 * flag, so a completed account with zero cloud Environments bounced between
 * /setup and /new forever, until the tab crashed on React error 185
 * (CMMD-Center/forge#3563). This reuses firstRunEnvironmentEntry, the same
 * rule useFirstAccountSetupRedirect already applies, so the two routes cannot
 * disagree again.
 *
 * The two callers treat a still-loading catalog oppositely, and both readings
 * are deliberate. useFirstAccountSetupRedirect treats loading as "assume an
 * Environment exists" so it does not flash a redirect to /setup on every
 * other route while the catalog is in flight. Here, on /setup itself, a
 * loading catalog means we do not yet know whether an Environment exists, so
 * we hold still rather than guess and risk leaving too early.
 */
export function shouldLeaveFirstAccountSetup(input: {
  readonly setupState: FirstAccountSetupState | null;
  readonly useCmmdSetupState: boolean;
  readonly cloudCatalog: { readonly isLoading: boolean; readonly hasEnvironment: boolean };
}): boolean {
  if (input.setupState === null) {
    return false;
  }
  if (!input.useCmmdSetupState) {
    return input.setupState.status === "completed";
  }
  if (input.cloudCatalog.isLoading) {
    return false;
  }
  return (
    firstRunEnvironmentEntry({
      hasSeat: true,
      hasEnvironmentId: input.cloudCatalog.hasEnvironment,
      setupCompleted: input.setupState.status === "completed",
    }).kind === "continue"
  );
}
